import React, { useEffect, useState } from 'react';
import { config } from '@/config';
import { GAME_KIND_LABELS } from '@/utils/groupGameBareme';
import Podium, { RankingList } from '@/components/games/Podium';

const API = config.BACKEND_URL;

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const RANKING_KIND_OPTIONS = [
  ['general', 'Général'],
  ...Object.entries(GAME_KIND_LABELS),
];

/** Sélecteur type « segmented control » (même API que les anciennes pastilles). */
export function FilterPills({ value, options, onChange, compact, align = 'end', nowrap = false }) {
  return (
    <div className={`flex ${align === 'start' ? 'justify-start' : 'justify-end'}`}>
      <div
        role="tablist"
        className={`inline-flex max-w-full ${nowrap ? 'flex-nowrap overflow-x-auto' : 'flex-wrap'} items-stretch rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-100/90 dark:bg-gray-900/70 p-0.5 gap-0.5`}
      >
        {options.map(([id, label]) => {
          const active = value === id;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(id)}
              className={[
                compact ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs',
                'whitespace-nowrap font-medium transition-colors rounded-md',
                active
                  ? 'bg-white dark:bg-gray-700 text-[#f76f4b] shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200',
              ].join(' ')}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function RankingFilters({ tab, onTabChange, kind, onKindChange, compact = false }) {
  return (
    <div className="flex flex-col items-end gap-1.5 min-w-0">
      <FilterPills
        value={tab}
        options={[['game', 'Ce jeu'], ['cumul', 'Cumul']]}
        onChange={onTabChange}
        compact={compact}
      />
      {tab === 'cumul' && (
        <FilterPills
          value={kind}
          options={RANKING_KIND_OPTIONS}
          onChange={onKindChange}
          compact
        />
      )}
    </div>
  );
}

export default function GameResultsPanel({
  instanceId,
  token,
  meId,
  compact = false,
  hideFilters = false,
  tab: tabProp,
  onTabChange,
  kind: kindProp,
  onKindChange,
}) {
  const [tabState, setTabState] = useState('game');
  const [kindState, setKindState] = useState('general');
  const [ranking, setRanking] = useState(null);
  const [cumul, setCumul] = useState(null);

  const tab = tabProp ?? tabState;
  const kind = kindProp ?? kindState;
  const setTab = onTabChange ?? setTabState;
  const setKind = onKindChange ?? setKindState;

  useEffect(() => {
    if (!token || !instanceId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const [r1, r2] = await Promise.all([
          fetch(`${API}/group/games/${instanceId}/ranking`, { headers: authHeaders(token) }),
          fetch(`${API}/group/games/cumulative`, { headers: authHeaders(token) }),
        ]);
        if (cancelled) return;
        if (r1.ok) setRanking(await r1.json());
        if (r2.ok) setCumul(await r2.json());
      } catch {
        /* ignore */
      }
    };
    load();
    return () => { cancelled = true; };
  }, [instanceId, token]);

  const rows = tab === 'game'
    ? (ranking?.rows || [])
    : kind === 'general'
      ? (cumul?.general || [])
      : (cumul?.by_kind?.[kind] || []);

  return (
    <div className={compact ? '' : 'mt-3'}>
      {!hideFilters && (
        <div className="flex justify-end mb-3">
          <RankingFilters
            tab={tab}
            onTabChange={setTab}
            kind={kind}
            onKindChange={setKind}
            compact={compact}
          />
        </div>
      )}
      <h4 className="text-[10px] uppercase tracking-wide text-gray-400 text-center mb-2">Classement</h4>
      <Podium
        rows={rows}
        compact={compact}
        showMoves={tab === 'game' && ranking?.kind === 'memory'}
        showElapsed={tab === 'game' && ranking?.kind && ranking.kind !== 'memory'}
      />
      <RankingList
        rows={rows}
        meId={meId}
        skip={3}
        showMoves={tab === 'game' && ranking?.kind === 'memory'}
        showElapsed={tab === 'game' && ranking?.kind && ranking.kind !== 'memory'}
      />
    </div>
  );
}
